import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import useStore from "../store/useStore";

/* ── Skeleton shown while banners load ── */
const MobileBannerSkeleton = () => (
  <section className="block sm:hidden w-full px-3 py-2">
    <div className="h-[180px] w-full animate-pulse rounded-2xl bg-gray-200" />
    <div className="mt-2.5 flex justify-center gap-1.5">
      <div className="h-1.5 w-5 rounded-full bg-gray-300" />
      <div className="h-1.5 w-1.5 rounded-full bg-gray-200" />
      <div className="h-1.5 w-1.5 rounded-full bg-gray-200" />
    </div>
  </section>
);

/* ── Gradient fallbacks when banner image fails ── */
const GRADIENTS = [
  "from-[#1a6b3c] via-[#2E7B52] to-[#1F5A3A]",
  "from-[#b45309] via-[#d97706] to-[#92400e]",
  "from-[#1e40af] via-[#3b82f6] to-[#1d4ed8]",
  "from-[#7c3aed] via-[#8b5cf6] to-[#6d28d9]",
  "from-[#be123c] via-[#e11d48] to-[#9f1239]",
];

/* ── Single mobile banner card ── */
const MobileBannerCard = ({ banner, index, imgErr, setImgErr, onClick }) => {
  const hasImage = banner.image && !imgErr[banner.id ?? index];
  const isClickable = Boolean(banner.url);
  const gradient = GRADIENTS[index % GRADIENTS.length];

  return (
    <div
      role={isClickable ? "button" : undefined}
      tabIndex={isClickable ? 0 : -1}
      onClick={() => onClick(banner)}
      onKeyDown={(event) => {
        if (isClickable && (event.key === "Enter" || event.key === " ")) {
          event.preventDefault();
          onClick(banner);
        }
      }}
      className={`relative h-[180px] w-full overflow-hidden rounded-2xl shadow-md ${
        isClickable ? "cursor-pointer" : "cursor-default"
      }`}
    >
      {hasImage ? (
        <>
          <img
            src={banner.image}
            alt={banner.title || `Banner ${index + 1}`}
            className="absolute inset-0 h-full w-full object-cover"
            onError={() =>
              setImgErr((prev) => ({
                ...prev,
                [banner.id ?? index]: true,
              }))
            }
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-transparent to-transparent" />
        </>
      ) : (
        <>
          <div className={`absolute inset-0 bg-gradient-to-br ${gradient}`} />
          <div
            className="absolute inset-0 opacity-20"
            style={{
              background:
                "radial-gradient(circle at 20% 50%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 80% 30%, rgba(255,255,255,0.1) 0%, transparent 40%)",
            }}
          />
        </>
      )}

      {banner.title && (
        <div className="absolute bottom-0 left-0 right-0 p-3">
          <p className="line-clamp-2 text-sm font-extrabold leading-snug text-white drop-shadow">
            {banner.title}
          </p>
        </div>
      )}
    </div>
  );
};

/* ── Mobile-only Banner Carousel (1 slide at a time, touch-swipe) ── */
const BannerCarouselMobile = () => {
  const { banners, banners_loading, fetchBanners } = useStore();
  const navigate = useNavigate();
  const [imgErr, setImgErr] = useState({});
  const [activeIndex, setActiveIndex] = useState(0);
  const swiperRef = useRef(null);

  useEffect(() => {
    fetchBanners();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (banners_loading && banners.length === 0) return <MobileBannerSkeleton />;
  if (!banners.length) return null;

  const handleBannerClick = (banner) => {
    if (banner.url) navigate(banner.url);
  };
  const canLoop = banners.length > 1;

  return (
    <section className="block sm:hidden w-full px-3 py-2">
      <div className="relative">
        <Swiper
          modules={[Autoplay, Pagination]}
          onSwiper={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
          slidesPerView={1}
          spaceBetween={12}
          loop={canLoop}
          autoplay={
            canLoop
              ? {
                  delay: 3000,
                  disableOnInteraction: false,
                }
              : false
          }
          speed={600}
          className="!overflow-hidden !rounded-2xl"
        >
          {banners.map((banner, index) => (
            <SwiperSlide key={banner.id ?? index}>
              <MobileBannerCard
                banner={banner}
                index={index}
                imgErr={imgErr}
                setImgErr={setImgErr}
                onClick={handleBannerClick}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

      {/* Dot indicators */}
      {canLoop && (
        <div className="mt-2.5 flex justify-center gap-1.5">
          {banners.map((banner, index) => (
            <button
              key={banner.id ?? index}
              type="button"
              onClick={() => swiperRef.current?.slideToLoop(index)}
              aria-label={`Go to banner ${index + 1}`}
              aria-current={index === activeIndex ? "true" : undefined}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                index === activeIndex
                  ? "w-5 bg-green-600"
                  : "w-1.5 bg-gray-300"
              }`}
            />
          ))}
        </div>
      )}
    </section>
  );
};

export default BannerCarouselMobile;
